import React from 'react'
import {
  CartesianGrid,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import styled from 'styled-components'

import {
  HOVER_DIMMED_OPACITY,
  HOVER_FOCUSED_OPACITY,
  HOVER_TRANSITION_MS,
  HOVER_TRANSLUCENT_BASE_OPACITY,
  HOVER_TRANSLUCENT_DIMMED_OPACITY,
} from 'features/charts-v2/chart-hover-constants'
import { ChartDatapointHoverAnnotation } from 'features/charts-v2/components/ChartDatapointHoverAnnotation'
import {
  RATING_THRESHOLDS,
  RATING_ZONE_COLORS,
  SENTIMENT_THRESHOLDS,
  SENTIMENT_ZONE_COLORS,
  SentimentColorGroup,
} from 'features/charts-v2/constants'
import { useBreakdownColorMap } from 'features/charts-v2/context/breakdown-color-map-context'
import { chartConfig } from 'features/charts-v2/services/chart-config-service'
import { dataTransformationService } from 'features/charts-v2/services/data-transformation-service'
import { BaseChartStrategy } from 'features/charts-v2/strategies/Base'
import { METRICS, type MetricType } from 'features/charts-v2/types'
import {
  ChartProps,
  TransformedChartData,
} from 'features/charts-v2/types/chart-strategy'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import {
  computePaddedScatterDomain,
  formatScatterAxisTick,
  generateScatterTicks,
} from 'features/charts-v2/utils/axis-calculations'
import { getMetricAxisDomain } from 'features/charts-v2/utils/axis-tick-utils'
import { buildBreakdownColorMap } from 'features/charts-v2/utils/breakdown-color-map'
import {
  computeWeightedAverage,
  getAugmentedBubbleMetrics,
  getWeightMetricForAxis,
} from 'features/charts-v2/utils/bubble-weighted-avg'
import { getMetricLabel } from 'features/charts-v2/utils/metric-format-utils'
import {
  computeVisibleLabels,
  getLabelThreshold,
  processLabel,
} from 'features/charts-v2/utils/process-label'

const ChartContainer = styled.div`
  width: 100%;
  height: 100%;

  /* Dim non-hovered bubbles when any bubble is hovered */
  &:has(circle[data-bubble-name]:hover) circle[data-bubble-name] {
    fill-opacity: ${HOVER_TRANSLUCENT_DIMMED_OPACITY};
    stroke-opacity: ${HOVER_DIMMED_OPACITY};
    transition:
      fill-opacity ${HOVER_TRANSITION_MS}ms ease,
      stroke-opacity ${HOVER_TRANSITION_MS}ms ease;
  }

  /* Keep the hovered bubble at its base translucent appearance */
  &:has(circle[data-bubble-name]:hover) circle[data-bubble-name]:hover {
    fill-opacity: ${HOVER_TRANSLUCENT_BASE_OPACITY} !important;
    stroke-opacity: ${HOVER_FOCUSED_OPACITY} !important;
  }
`

interface BubbleDataPoint {
  name: string
  x: number
  y: number
  z: number
  recordCount?: number
  sentenceCount?: number
}

interface ScatterShapeProps {
  cx: number
  cy: number
  payload: BubbleDataPoint
}

interface BubbleChartViewProps {
  data: ChartProps['data']
  /** Unfiltered dataset for stable label→color mapping; see ChartProps.colorReferenceData. */
  colorReferenceData?: ChartProps['colorReferenceData']
  transformedData: TransformedChartData
  selectedMetrics: MetricType[]
  selectedBreakdowns?: ChartProps['selectedBreakdowns']
  onDataPointHover?: (name: string | null) => void
  onSegmentClick?: ChartProps['onSegmentClick']
  fontSizePx: number
  showXAxisLabel: boolean
  showYAxisLabel: boolean
  showDatapointLabels: boolean
  showSentimentZones: boolean
  showRatingZones: boolean
  xAxisDomain?: [number, number]
  yAxisDomain?: [number, number]
  disableAnimation: boolean
}

// eslint-disable-next-line react-refresh/only-export-components -- internal helper component, not a public export
const BubbleChartViewInner: React.FC<BubbleChartViewProps> = ({
  data,
  colorReferenceData,
  transformedData,
  selectedMetrics,
  selectedBreakdowns,
  onDataPointHover,
  onSegmentClick,
  fontSizePx,
  showXAxisLabel,
  showYAxisLabel,
  showDatapointLabels,
  showSentimentZones,
  showRatingZones,
  xAxisDomain,
  yAxisDomain,
  disableAnimation,
}) => {
  const contextColorMap = useBreakdownColorMap()
  const colorMap =
    contextColorMap.size > 0
      ? contextColorMap
      : buildBreakdownColorMap(
          selectedBreakdowns?.[0] ?? null,
          colorReferenceData ?? data,
        )
  const defaultColor = chartConfig.getColor('primary', 0)

  const bubbleData = transformedData.chartData as unknown as BubbleDataPoint[]

  // Dynamic labels based on selectedMetrics order
  const xAxisLabel = selectedMetrics[0]!
    ? getMetricLabel(selectedMetrics[0]!)
    : 'X-Axis'
  const yAxisLabel = selectedMetrics[1]!
    ? getMetricLabel(selectedMetrics[1]!)
    : 'Y-Axis'
  const hasSizeMetric = selectedMetrics.length >= 3
  const sizeLabel = hasSizeMetric ? getMetricLabel(selectedMetrics[2]!) : null

  // Check which axis has sentiment
  const xAxisIsSentiment = selectedMetrics[0] === METRICS.SENTIMENT.id
  const yAxisIsSentiment = selectedMetrics[1] === METRICS.SENTIMENT.id

  // Check which axis has rating
  const xAxisIsRating = selectedMetrics[0] === METRICS.RATING.id
  const yAxisIsRating = selectedMetrics[1] === METRICS.RATING.id

  // Compute effective domains based on metric type and data
  // If xAxisDomain/yAxisDomain are provided (from Container), use them.
  // Otherwise, compute from actual bubble data with padding and clamping.
  const computeDomainFromData = (
    bubbles: BubbleDataPoint[],
    accessor: (d: BubbleDataPoint) => number,
    metricType?: MetricType,
  ): [number, number] | undefined => {
    if (bubbles.length === 0) return undefined
    const values = bubbles.map(accessor)
    const min = Math.min(...values)
    const max = Math.max(...values)
    return computePaddedScatterDomain([min, max], metricType)
  }

  const effectiveXAxisDomain: [number, number] = xAxisDomain ??
    computeDomainFromData(bubbleData, (d) => d.x, selectedMetrics[0]) ?? [0, 1]

  const effectiveYAxisDomain: [number, number] = yAxisDomain ??
    computeDomainFromData(bubbleData, (d) => d.y, selectedMetrics[1]) ?? [0, 1]

  const xTicks = generateScatterTicks(
    effectiveXAxisDomain[0],
    effectiveXAxisDomain[1],
    selectedMetrics[0],
  )
  const yTicks = generateScatterTicks(
    effectiveYAxisDomain[0],
    effectiveYAxisDomain[1],
    selectedMetrics[1],
  )

  // Extend Y domain to encompass all ticks (Y-axis niceMin/niceMax may go beyond data domain)
  const extendedYDomain: [number, number] = [
    Math.min(effectiveYAxisDomain[0], yTicks[0] ?? effectiveYAxisDomain[0]),
    Math.max(
      effectiveYAxisDomain[1],
      yTicks[yTicks.length - 1] ?? effectiveYAxisDomain[1],
    ),
  ]

  // Extend X domain to encompass all ticks (X-axis nice values may go beyond data domain)
  const extendedXDomain: [number, number] = [
    Math.min(effectiveXAxisDomain[0], xTicks[0] ?? effectiveXAxisDomain[0]),
    Math.max(
      effectiveXAxisDomain[1],
      xTicks[xTicks.length - 1] ?? effectiveXAxisDomain[1],
    ),
  ]

  // Clamp extended domains to metric bounds to prevent rogue ticks from overshooting
  const yMetricBounds = getMetricAxisDomain(selectedMetrics[1])
  if (yMetricBounds) {
    extendedYDomain[0] = Math.max(extendedYDomain[0], yMetricBounds[0])
    extendedYDomain[1] = Math.min(extendedYDomain[1], yMetricBounds[1])
  }

  const xMetricBounds = getMetricAxisDomain(selectedMetrics[0])
  if (xMetricBounds) {
    extendedXDomain[0] = Math.max(extendedXDomain[0], xMetricBounds[0])
    extendedXDomain[1] = Math.min(extendedXDomain[1], xMetricBounds[1])
  }

  // Calculate average values for reference lines using volume-weighted averaging
  // to match V1 behavior (rating weighted by record count, sentiment by sentence count)
  const xWeightField =
    getWeightMetricForAxis(selectedMetrics[0]!) === 'sentence_count'
      ? 'sentenceCount'
      : 'recordCount'
  const yWeightField =
    getWeightMetricForAxis(selectedMetrics[1]!) === 'sentence_count'
      ? 'sentenceCount'
      : 'recordCount'

  const avgX = computeWeightedAverage(
    bubbleData.map((d) => d.x),
    bubbleData.map((d) => ((d[xWeightField] ?? d.z) as number) || 1),
  )
  const avgY = computeWeightedAverage(
    bubbleData.map((d) => d.y),
    bubbleData.map((d) => ((d[yWeightField] ?? d.z) as number) || 1),
  )

  // Calculate sentiment zones
  const sentimentZones: Array<{
    x1?: number
    x2?: number
    y1?: number
    y2?: number
    fill: string
  }> = []

  if (showSentimentZones && (xAxisIsSentiment || yAxisIsSentiment)) {
    if (xAxisIsSentiment) {
      // Vertical zones for X-axis sentiment
      const domainMin = extendedXDomain[0]
      const domainMax = extendedXDomain[1]

      // Negative zone (left side, < -0.3)
      if (domainMin < SENTIMENT_THRESHOLDS.NEGATIVE) {
        const x1 = Math.max(domainMin, -1)
        const x2 = Math.min(domainMax, SENTIMENT_THRESHOLDS.NEGATIVE)
        if (x2 > x1) {
          sentimentZones.push({
            x1,
            x2,
            fill: SENTIMENT_ZONE_COLORS[SentimentColorGroup.NEGATIVE],
          })
        }
      }

      // Neutral zone (middle, -0.3 to 0.3)
      if (
        domainMin < SENTIMENT_THRESHOLDS.POSITIVE &&
        domainMax > SENTIMENT_THRESHOLDS.NEGATIVE
      ) {
        const x1 = Math.max(domainMin, SENTIMENT_THRESHOLDS.NEGATIVE)
        const x2 = Math.min(domainMax, SENTIMENT_THRESHOLDS.POSITIVE)
        if (x2 > x1) {
          sentimentZones.push({
            x1,
            x2,
            fill: SENTIMENT_ZONE_COLORS[SentimentColorGroup.NEUTRAL],
          })
        }
      }

      // Positive zone (right side, > 0.3)
      if (domainMax > SENTIMENT_THRESHOLDS.POSITIVE) {
        const x1 = Math.max(domainMin, SENTIMENT_THRESHOLDS.POSITIVE)
        const x2 = Math.min(domainMax, 1)
        if (x2 > x1) {
          sentimentZones.push({
            x1,
            x2,
            fill: SENTIMENT_ZONE_COLORS[SentimentColorGroup.POSITIVE],
          })
        }
      }
    } else if (yAxisIsSentiment) {
      // Horizontal zones for Y-axis sentiment
      const domainMin = extendedYDomain[0]
      const domainMax = extendedYDomain[1]

      // Negative zone (bottom, < -0.3)
      if (domainMin < SENTIMENT_THRESHOLDS.NEGATIVE) {
        const y1 = Math.max(domainMin, -1)
        const y2 = Math.min(domainMax, SENTIMENT_THRESHOLDS.NEGATIVE)
        if (y2 > y1) {
          sentimentZones.push({
            y1,
            y2,
            fill: SENTIMENT_ZONE_COLORS[SentimentColorGroup.NEGATIVE],
          })
        }
      }

      // Neutral zone (middle, -0.3 to 0.3)
      if (
        domainMin < SENTIMENT_THRESHOLDS.POSITIVE &&
        domainMax > SENTIMENT_THRESHOLDS.NEGATIVE
      ) {
        const y1 = Math.max(domainMin, SENTIMENT_THRESHOLDS.NEGATIVE)
        const y2 = Math.min(domainMax, SENTIMENT_THRESHOLDS.POSITIVE)
        if (y2 > y1) {
          sentimentZones.push({
            y1,
            y2,
            fill: SENTIMENT_ZONE_COLORS[SentimentColorGroup.NEUTRAL],
          })
        }
      }

      // Positive zone (top, > 0.3)
      if (domainMax > SENTIMENT_THRESHOLDS.POSITIVE) {
        const y1 = Math.max(domainMin, SENTIMENT_THRESHOLDS.POSITIVE)
        const y2 = Math.min(domainMax, 1)
        if (y2 > y1) {
          sentimentZones.push({
            y1,
            y2,
            fill: SENTIMENT_ZONE_COLORS[SentimentColorGroup.POSITIVE],
          })
        }
      }
    }
  }

  // Calculate rating zones
  if (showRatingZones && (xAxisIsRating || yAxisIsRating)) {
    if (xAxisIsRating) {
      // Vertical zones for X-axis rating
      const domainMin = extendedXDomain[0]
      const domainMax = extendedXDomain[1]

      if (domainMin < RATING_THRESHOLDS.NEGATIVE) {
        const x1 = Math.max(domainMin, 1)
        const x2 = Math.min(domainMax, RATING_THRESHOLDS.NEGATIVE)
        if (x2 > x1) {
          sentimentZones.push({
            x1,
            x2,
            fill: RATING_ZONE_COLORS.negative,
          })
        }
      }

      // Positive zone (right side, > 3.5)
      if (domainMax > RATING_THRESHOLDS.POSITIVE) {
        const x1 = Math.max(domainMin, RATING_THRESHOLDS.POSITIVE)
        const x2 = Math.min(domainMax, 5)
        if (x2 > x1) {
          sentimentZones.push({
            x1,
            x2,
            fill: RATING_ZONE_COLORS.positive,
          })
        }
      }
    } else if (yAxisIsRating) {
      // Horizontal zones for Y-axis rating
      const domainMin = extendedYDomain[0]
      const domainMax = extendedYDomain[1]

      if (domainMin < RATING_THRESHOLDS.NEGATIVE) {
        const y1 = Math.max(domainMin, 1)
        const y2 = Math.min(domainMax, RATING_THRESHOLDS.NEGATIVE)
        if (y2 > y1) {
          sentimentZones.push({
            y1,
            y2,
            fill: RATING_ZONE_COLORS.negative,
          })
        }
      }

      // Positive zone (top, > 3.5)
      if (domainMax > RATING_THRESHOLDS.POSITIVE) {
        const y1 = Math.max(domainMin, RATING_THRESHOLDS.POSITIVE)
        const y2 = Math.min(domainMax, 5)
        if (y2 > y1) {
          sentimentZones.push({
            y1,
            y2,
            fill: RATING_ZONE_COLORS.positive,
          })
        }
      }
    }
  }

  // Get short metric names for reference line labels
  const getShortMetricLabel = (metricType: MetricType): string => {
    switch (metricType) {
      case METRICS.SENTIMENT.id:
        return 'Sentiment'
      case METRICS.RATING.id:
        return 'Rating'
      case METRICS.SENTENCE_COUNT.id:
        return 'Sentences'
      case METRICS.RECORD_COUNT.id:
        return 'Records'
      case METRICS.NET_PROMOTER_SCORE.id:
        return 'NPS'
      default:
        return metricType
    }
  }

  const xMetricName = selectedMetrics[0]!
    ? getShortMetricLabel(selectedMetrics[0]!)
    : 'X'
  const yMetricName = selectedMetrics[1]!
    ? getShortMetricLabel(selectedMetrics[1]!)
    : 'Y'

  const MIN_RADIUS = 12
  const MAX_RADIUS = 50
  // In 2-metric mode there is no size dimension; match Scatter.tsx's fixed radius.
  const SCATTER_RADIUS = 6

  const zValues = bubbleData.map((d) => d.z)
  const minZ = Math.min(...zValues)
  const maxZ = Math.max(...zValues)
  const zRange = maxZ - minZ

  const radiusFor = (z: number): number => {
    if (!hasSizeMetric) return SCATTER_RADIUS
    const normalizedZ = zRange > 0 ? (z - minZ) / zRange : 0.5
    return MIN_RADIUS + normalizedZ * (MAX_RADIUS - MIN_RADIUS)
  }

  // Pre-compute visible labels to avoid mutation during render
  const ESTIMATED_CHART_WIDTH = 800
  const ESTIMATED_CHART_HEIGHT = 500
  const xRange = extendedXDomain[1] - extendedXDomain[0]
  const yRange = extendedYDomain[1] - extendedYDomain[0]

  const visibleLabels = computeVisibleLabels(
    bubbleData,
    fontSizePx,
    (d) =>
      xRange > 0
        ? ((d.x - extendedXDomain[0]) / xRange) * ESTIMATED_CHART_WIDTH
        : 0,
    (d) =>
      yRange > 0
        ? ESTIMATED_CHART_HEIGHT -
          ((d.y - extendedYDomain[0]) / yRange) * ESTIMATED_CHART_HEIGHT
        : 0,
    (d) => radiusFor(d.z),
  )

  return (
    <ChartContainer>
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart
          data={bubbleData}
          margin={{
            top: 60,
            right: 60,
            bottom: 60,
            left: 60,
          }}
          onMouseLeave={() => onDataPointHover?.(null)}
        >
          {/* Sentiment background zones */}
          {sentimentZones.map((zone, index) => (
            <ReferenceArea
              key={`sentiment-zone-${index}`}
              x1={zone.x1}
              x2={zone.x2}
              y1={zone.y1}
              y2={zone.y2}
              fill={zone.fill}
              fillOpacity={1}
            />
          ))}

          <CartesianGrid strokeDasharray="3 3" />

          {/* Average reference lines */}
          {bubbleData && bubbleData.length > 0 && (
            <>
              {/* Vertical line for X-axis average */}
              <ReferenceLine
                x={avgX}
                stroke="#475569"
                strokeWidth={2}
                label={{
                  value: `Average ${xMetricName}: ${formatScatterAxisTick(avgX, selectedMetrics[0])}`,
                  position: 'top',
                  fill: '#1e293b',
                  fontSize: fontSizePx,
                  fontWeight: 500,
                }}
              />
              {/* Horizontal line for Y-axis average */}
              <ReferenceLine
                y={avgY}
                stroke="#475569"
                strokeWidth={2}
                label={{
                  value: `Average ${yMetricName}: ${formatScatterAxisTick(avgY, selectedMetrics[1])}`,
                  position: 'insideTopLeft',
                  fill: '#1e293b',
                  fontSize: fontSizePx,
                  fontWeight: 500,
                }}
              />
            </>
          )}

          <XAxis
            type="number"
            dataKey="x"
            name={xAxisLabel}
            domain={extendedXDomain}
            ticks={xTicks}
            tickFormatter={(value) =>
              formatScatterAxisTick(value, selectedMetrics[0])
            }
            tick={{ fontSize: fontSizePx }}
            allowDataOverflow={false}
            label={
              showXAxisLabel
                ? {
                    value: xAxisLabel,
                    position: 'insideBottom',
                    offset: -5,
                    style: {
                      textAnchor: 'middle',
                      fill: '#666',
                      fontSize: fontSizePx,
                    },
                  }
                : undefined
            }
          />
          <YAxis
            type="number"
            dataKey="y"
            name={yAxisLabel}
            domain={extendedYDomain}
            ticks={yTicks}
            tickFormatter={(value) =>
              formatScatterAxisTick(value, selectedMetrics[1])
            }
            tick={{ fontSize: fontSizePx }}
            allowDataOverflow={false}
            label={
              showYAxisLabel
                ? {
                    value: yAxisLabel,
                    angle: -90,
                    position: 'insideLeft',
                    style: {
                      textAnchor: 'middle',
                      fill: '#666',
                      fontSize: fontSizePx,
                    },
                  }
                : undefined
            }
          />
          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload[0]) {
                const tooltipData = payload[0].payload
                const rows = [
                  {
                    label: xAxisLabel,
                    value:
                      tooltipData.x != null ? tooltipData.x.toFixed(2) : 'N/A',
                    percentage: null,
                    color: null,
                  },
                  {
                    label: yAxisLabel,
                    value:
                      tooltipData.y != null ? tooltipData.y.toFixed(2) : 'N/A',
                    percentage: null,
                    color: null,
                  },
                  ...(sizeLabel
                    ? [
                        {
                          label: sizeLabel,
                          value:
                            tooltipData.z != null
                              ? tooltipData.z.toLocaleString()
                              : 'N/A',
                          percentage: null,
                          color: null,
                        },
                      ]
                    : []),
                ]
                return (
                  <ChartDatapointHoverAnnotation
                    title={tooltipData.name}
                    rows={rows}
                    footer={null}
                    focusedField={null}
                  />
                )
              }
              return null
            }}
          />
          <Scatter
            dataKey="z"
            name="Themes"
            isAnimationActive={!showDatapointLabels && !disableAnimation}
            onClick={(
              clickData: BubbleDataPoint,
              _index: number,
              event: React.MouseEvent,
            ) => {
              if (onSegmentClick && clickData) {
                onSegmentClick(
                  {
                    primaryBreakdown: clickData.name,
                    value: clickData.z || clickData.y || clickData.x || 0,
                    payload: clickData as unknown as Record<
                      string,
                      string | number | boolean | null
                    >,
                  },
                  event,
                )
              }
            }}
            shape={
              ((scatterProps: ScatterShapeProps) => {
                const radius = radiusFor(scatterProps.payload.z)

                // Check if this label should be visible (pre-computed)
                const shouldShowLabel =
                  showDatapointLabels &&
                  visibleLabels.has(scatterProps.payload?.name)
                const wrappedLines = shouldShowLabel
                  ? processLabel(
                      getLabelThreshold(fontSizePx),
                      scatterProps.payload.name,
                    ).split('\n')
                  : []

                const pointColor =
                  colorMap.get(scatterProps.payload.name) ?? defaultColor

                return (
                  <g>
                    <circle
                      cx={scatterProps.cx}
                      cy={scatterProps.cy}
                      r={radius}
                      fill={pointColor}
                      stroke={pointColor}
                      strokeWidth={2}
                      fillOpacity={HOVER_TRANSLUCENT_BASE_OPACITY}
                      strokeOpacity={0.9}
                      data-bubble-name={scatterProps.payload.name}
                      onMouseEnter={() =>
                        onDataPointHover?.(scatterProps.payload.name)
                      }
                      onMouseLeave={() => onDataPointHover?.(null)}
                      style={{ cursor: 'pointer' }}
                    />
                    {shouldShowLabel && (
                      <text
                        x={scatterProps.cx}
                        y={scatterProps.cy - radius - 8}
                        fill="#333"
                        fontSize={fontSizePx}
                        textAnchor="middle"
                        style={{
                          fontFamily:
                            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                          pointerEvents: 'none',
                        }}
                      >
                        {wrappedLines.map((line, idx) => (
                          <tspan
                            key={idx}
                            x={scatterProps.cx}
                            dy={idx === 0 ? 0 : fontSizePx * 0.8}
                          >
                            {line}
                          </tspan>
                        ))}
                      </text>
                    )}
                  </g>
                )
              }) as never
            }
          />
        </ScatterChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}

const BubbleChartView = React.memo(BubbleChartViewInner)

export class BubbleChartStrategy extends BaseChartStrategy {
  public readonly chartType = ChartTypeV2.BUBBLE
  public readonly requiredMetrics: MetricType[] = [
    METRICS.SENTIMENT.id,
    METRICS.RATING.id,
    METRICS.SENTENCE_COUNT.id,
  ]
  public readonly optionalMetrics: MetricType[] = []

  public transform(props: ChartProps): TransformedChartData {
    const breakdowns = props.selectedBreakdowns || []

    // Handle 0 breakdowns: show a single bubble at the average of x/y.
    if (breakdowns.length === 0 && props.data && props.data.data.length > 0) {
      const totalRecords = props.data.data.length
      const hasSizeMetric = props.selectedMetrics.length >= 3
      let sumX = 0
      let sumY = 0
      let sumZ = 0

      props.data.data.forEach((item) => {
        if (item.values[0] != null) sumX += item.values[0]
        if (item.values[1] != null) sumY += item.values[1]
        if (hasSizeMetric && item.values[2] != null) sumZ += item.values[2]
      })

      const avgX = totalRecords > 0 ? sumX / totalRecords : 0
      const avgY = totalRecords > 0 ? sumY / totalRecords : 0

      return {
        chartData: [
          {
            name: 'Total',
            x: avgX,
            y: avgY,
            z: hasSizeMetric ? sumZ : 1,
            sentenceCount: sumZ,
          },
        ],
      }
    }

    return dataTransformationService.transformForBubbleChart(
      props.data!,
      props.selectedMetrics,
      props.timeBucket,
      getAugmentedBubbleMetrics(props.selectedMetrics),
    )
  }

  public render(
    props: ChartProps & { transformedData: TransformedChartData },
  ): React.ReactElement {
    const validation = this.validate(props)
    if (!validation.isValid) {
      return this.renderValidationError(validation)
    }

    if (
      !props.transformedData.chartData ||
      props.transformedData.chartData.length === 0
    ) {
      return this.renderEmptyState(
        'No data available',
        'Try adding some filters.',
      )
    }

    return (
      <BubbleChartView
        data={props.data}
        colorReferenceData={props.colorReferenceData}
        transformedData={props.transformedData}
        selectedMetrics={props.selectedMetrics}
        selectedBreakdowns={props.selectedBreakdowns}
        onDataPointHover={props.onDataPointHover}
        onSegmentClick={props.onSegmentClick}
        fontSizePx={props.fontSizePx ?? 13}
        showXAxisLabel={props.showXAxisLabel ?? true}
        showYAxisLabel={props.showYAxisLabel ?? true}
        showDatapointLabels={props.showDatapointLabels ?? true}
        showSentimentZones={props.showSentimentZones ?? true}
        showRatingZones={props.showRatingZones ?? true}
        xAxisDomain={props.xAxisDomain}
        yAxisDomain={props.yAxisDomain}
        disableAnimation={props.disableAnimation ?? false}
      />
    )
  }

  public getTitle(props?: ChartProps): string {
    if (!props || !props.selectedMetrics || props.selectedMetrics.length < 2) {
      return 'Bubble Chart'
    }

    const yLabel = getMetricLabel(props.selectedMetrics[1]!)
    const xLabel = getMetricLabel(props.selectedMetrics[0]!)
    return `${yLabel} vs. ${xLabel}`
  }
}
